import {
  selectActualFeatureFlags,
  selectDefaultIncidentDateFilter,
  selectIsAllCompaniesSelected,
  selectPartner,
  selectSelfFeatureFlags,
  selectSelfPermissions,
} from 'containers/App/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { ALL_TIMES, INCIDENT_DATE_FILTERS_OPTIONS, LAST_7_DAYS, LAST_24_HOURS } from './constants'

export const selectRegularFilters = (state) => state.resolutioncenter.incidentFilters.regularFilters

export const selectAssigneesOptions = (state) => state.resolutioncenter.incidentFilters.assignees

export const selectIsRegularFilterActive = (state) =>
  !!Object.keys(state.resolutioncenter.incidentFilters.regularFilters).filter(
    (key) => state.resolutioncenter.incidentFilters.regularFilters[key]?.length
  ).length

/**
 * "All Companies" for Bubble Ups / Auto Themis visibility.
 *
 * Under the incidents redesign FF the RC side Menu is not mounted, so it never applies a default
 * companyFilter. The redesign company Combobox then displays "All Companies" when
 * partnerOptions.selectedCompany is null — treat that the same as an explicit All Companies
 * selection (value === ''), otherwise the filters stay hidden while the UI looks correct.
 */
const selectIsAllCompaniesForImFilters = (state) => {
  if (selectIsAllCompaniesSelected(state)) return true
  return !!selectPartner(state) && selectCompanyFilter(state) == null
}

/**
 * Today's filters for IM Service Company are:
 *   Bubble Ups
 *   Auto Themis (below TH)
 */
const selectShowFilterToIMServiceCompany = (state) => (filterSelector) =>
  !!selectSelfFeatureFlags(state)?.IM_SERVICE_COMPANY &&
  (!!filterSelector(state) || selectIsAllCompaniesForImFilters(state))

const selectActualPotentialReportsFilter = (state) =>
  selectActualFeatureFlags(state).ENABLE_POTENTIAL_REPORTS ||
  selectActualFeatureFlags(state).ENABLE_POTENTIAL_REPORTS_V2

const selectActualAutoThemisFilter = (state) => selectActualFeatureFlags(state).ENABLE_AUTO_THEMIS

export const selectAllowSeeingPotentialIncidents = (state) =>
  selectShowFilterToIMServiceCompany(state)(selectActualPotentialReportsFilter)

export const selectAllowSeeingAutoThemis = (state) =>
  selectShowFilterToIMServiceCompany(state)(selectActualAutoThemisFilter)

export const selectDateFiltersOptions = (state) => {
  const ff = selectActualFeatureFlags(state)
  if (ff.POV_COSTUMER_FF) {
    return INCIDENT_DATE_FILTERS_OPTIONS.filter((option) => option.value !== ALL_TIMES)
  }
  return INCIDENT_DATE_FILTERS_OPTIONS
}

export const selectDefaultIncidentDateFilterWithDefault = (state) => {
  const ff = selectActualFeatureFlags(state)
  const defaultFromCompany = selectDefaultIncidentDateFilter(state)
  if (defaultFromCompany) return defaultFromCompany
  return ff.POV_COSTUMER_FF ? LAST_24_HOURS : LAST_7_DAYS
}

/**
 * User-scoped favorite from account status (`userDefaultIncidentDateFilter`).
 * `null` / missing means the user has never set a personal favorite — the UI shows an
 * inactive star on the company default until they click.
 */
export const selectUserDefaultIncidentDateFilter = (state) => {
  const value = state.app?.data?.userDefaultIncidentDateFilter
  return value == null ? null : value
}

/** Prefer the user's favorite; fall back to company (then POV/7d) default. */
export const selectEffectiveIncidentDateFilter = (state) => {
  const userDefault = selectUserDefaultIncidentDateFilter(state)
  if (userDefault != null) return userDefault
  return selectDefaultIncidentDateFilterWithDefault(state)
}

/**
 * Selector to determine if user should see the Phishing SOC Agent filter
 * Only users with access to the Phishing SOC Agent tool should be able to view and use this filter.
 * Master CNC users (IM_SERVICE_COMPANY) should be able to view it on all tenants.
 */
export const selectAllowSeeingPhishingSocAgent = (state) => {
  const ff = selectActualFeatureFlags(state)
  const selfFf = selectSelfFeatureFlags(state)
  const selfPermissions = selectSelfPermissions(state)
  const isAllCompaniesSelected = selectIsAllCompaniesSelected(state)

  // Users without phishingSocAccess permission have no access to the tool, so no point showing the filter
  if (
    selfPermissions?.phishingSocAccess === false ||
    selfPermissions?.phishing_soc_access === false
  ) {
    return false
  }

  // Master CNC users (IM_SERVICE_COMPANY) can see it on all tenants
  if (selfFf.IM_SERVICE_COMPANY) {
    return true
  }

  // Users with ENABLE_THEMIS_AI_ANALYSIS, POV_COSTUMER_FF, or ENABLE_THEMIS_AI_TRIAL_CAMPAIGN can see the filter
  // But not when "All Companies" is selected (unless they have ENABLE_THEMIS_AI_ANALYSIS or POV_COSTUMER_FF)
  if (isAllCompaniesSelected) {
    return (ff?.ENABLE_THEMIS_AI_ANALYSIS && ff?.phishSoc) || ff?.POV_COSTUMER_FF
  }

  return (
    (ff?.ENABLE_THEMIS_AI_ANALYSIS && ff?.phishSoc) ||
    ff?.POV_COSTUMER_FF ||
    ff?.ENABLE_THEMIS_AI_TRIAL_CAMPAIGN
  )
}
